<template>
  <main class="min-h-screen bg-gradient-to-b from-background to-background/95   overflow-hidden">
    <UnderwaterBg class="fixed inset-0 z-0 opacity-75" />
    
    <div class="relative z-10 p-4 sm:p-6 max-w-7xl mx-auto firefox-optimize">
      <div class="max-w-7xl mx-auto space-y-6">
        <!-- Header Section with Demo Badge -->
        <div class="relative">
          <admin-header 
            username="DemoUser"
            @create-cruise="dialogState.createCruise = true"
            @logout="handleLogout"
          />
          <div class="absolute top-0 right-0 bg-warning px-3 py-1 rounded-bl-lg rounded-tr-lg text-white font-bold text-sm">
            DEMO MODE
          </div>
        </div>

        <!-- Duck Activity Section (Demo) -->
        <div class="mb-6">
          <div 
            class="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 sm:p-6 cursor-pointer hover:shadow-md transition-all duration-200"
            @click="toggleDuckActivity"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <v-icon color="primary" size="24">mdi-duck</v-icon>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-blue-900">Your Duck Activity</h3>
                  <div class="flex flex-wrap gap-x-4 gap-y-1 text-sm text-blue-700">
                    <span class="inline-flex items-center gap-2">
                      <span><span class="font-medium">8</span> found</span>
                      <span class="inline-flex items-center gap-1 text-amber-700">
                        <v-icon size="16" color="warning">mdi-trophy</v-icon>
                        <span class="font-semibold">15</span>
                      </span>
                    </span>
                    <span class="inline-flex items-center gap-2">
                      <span><span class="font-medium">5</span> hidden</span>
                      <span class="inline-flex items-center gap-1 text-amber-700">
                        <v-icon size="16" color="warning">mdi-trophy</v-icon>
                        <span class="font-semibold">12</span>
                      </span>
                    </span>
                  </div>
                  <!-- Demo Leaderboard Ranking Chips -->
                  <div class="flex flex-wrap gap-2 mt-2">
                    <div class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-100 text-emerald-800 rounded-lg text-xs font-semibold border border-emerald-200">
                      <v-icon size="14" color="success">mdi-magnify</v-icon>
                      <span>Finder</span>
                      <span class="bg-emerald-200 text-emerald-900 px-1.5 py-0.5 rounded text-xs font-bold">
                        #3
                      </span>
                    </div>
                    <div class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-100 text-orange-800 rounded-lg text-xs font-semibold border border-orange-200">
                      <v-icon size="14" color="warning">mdi-eye-off</v-icon>
                      <span>Hider</span>
                      <span class="bg-orange-200 text-orange-900 px-1.5 py-0.5 rounded text-xs font-bold">
                        #7
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <router-link
                  :to="{ path: '/', hash: '#leaderboard' }"
                  class="inline-flex items-center px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg text-xs font-medium border border-amber-200 shadow-sm"
                  title="View leaderboard"
                  @click.stop
                >
                  <v-icon size="16" class="mr-1" color="warning">mdi-trophy</v-icon>
                  Leaderboard
                </router-link>
                <v-icon 
                  :class="{ 'rotate-180': showDuckActivity }"
                  class="transition-transform duration-200"
                  color="primary"
                >
                  mdi-chevron-down
                </v-icon>
              </div>
            </div>
          </div>

          <!-- Collapsible Duck Activity Details (Demo) -->
          <div v-if="showDuckActivity" class="mt-4 space-y-4">
            <!-- Found Ducks by Rarity -->
            <div class="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6">
              <div class="flex items-center gap-3 mb-4">
                <div class="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <v-icon color="success" size="20">mdi-treasure-chest</v-icon>
                </div>
                <div>
                  <h4 class="text-lg font-semibold text-gray-900">Ducks Found</h4>
                  <p class="text-sm text-gray-600">
                    8 ducks discovered by rarity
                    <span class="ml-2 inline-flex items-center gap-1 text-amber-700">
                      <v-icon size="16" color="warning">mdi-trophy</v-icon>
                      <span class="font-medium">15</span>
                    </span>
                  </p>
                </div>
              </div>
              
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div class="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <img src="/duckRarity/COMMON.webp" alt="Common Duck" class="w-12 h-12 object-contain" />
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-900">Common</span>
                      <span class="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                        5
                      </span>
                    </div>
                    <p class="text-sm text-gray-600">Found</p>
                  </div>
                </div>
                
                <div class="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <img src="/duckRarity/RARE.webp" alt="Rare Duck" class="w-12 h-12 object-contain" />
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-900">Rare</span>
                      <span class="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                        2
                      </span>
                    </div>
                    <p class="text-sm text-gray-600">Found</p>
                  </div>
                </div>
                
                <div class="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                  <img src="/duckRarity/LEGENDARY.webp" alt="Legendary Duck" class="w-12 h-12 object-contain" />
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-900">Legendary</span>
                      <span class="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full font-medium">
                        1
                      </span>
                    </div>
                    <p class="text-sm text-gray-600">Found</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Hidden Ducks by Rarity -->
            <div class="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6">
              <div class="flex items-center gap-3 mb-4">
                <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <v-icon color="primary" size="20">mdi-map-marker-plus</v-icon>
                </div>
                <div>
                  <h4 class="text-lg font-semibold text-gray-900">Ducks Hidden</h4>
                  <p class="text-sm text-gray-600">
                    5 ducks placed for others to find
                    <span class="ml-2 inline-flex items-center gap-1 text-amber-700">
                      <v-icon size="16" color="warning">mdi-trophy</v-icon>
                      <span class="font-medium">12</span>
                    </span>
                  </p>
                </div>
              </div>
              
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div class="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <img src="/duckRarity/COMMON.webp" alt="Common Duck" class="w-12 h-12 object-contain" />
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-900">Common</span>
                      <span class="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                        2
                      </span>
                    </div>
                    <p class="text-sm text-gray-600">Hidden</p>
                  </div>
                </div>
                
                <div class="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <img src="/duckRarity/RARE.webp" alt="Rare Duck" class="w-12 h-12 object-contain" />
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-900">Rare</span>
                      <span class="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                        2
                      </span>
                    </div>
                    <p class="text-sm text-gray-600">Hidden</p>
                  </div>
                </div>
                
                <div class="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                  <img src="/duckRarity/LEGENDARY.webp" alt="Legendary Duck" class="w-12 h-12 object-contain" />
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-900">Legendary</span>
                      <span class="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full font-medium">
                        1
                      </span>
                    </div>
                    <p class="text-sm text-gray-600">Hidden</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Duck View Info Card -->
        <div v-if="!loading && cruises.length > 0" 
          class="bg-blue-50/80  backdrop-blur-sm border border-blue-200  rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4"
        >
          <div class="flex items-center gap-3">
            <v-icon icon="mdi-information-outline" color="info" class="mr-3" size="large"></v-icon>
            <div>
              <h3 class="text-lg font-semibold text-gray-800 ">Duck Explorer View</h3>
              <p class="text-sm text-gray-600 ">Click on the eye icon <v-icon size="small">mdi-eye</v-icon> next to any duck to see the visitor view</p>
            </div>
          </div>
          <p class="text-xs text-gray-500  bg-white/50  p-2 rounded">This is what people will see when they scan a duck's QR code</p>
        </div>

        <!-- Empty State -->
        <div v-if="!loading && cruises.length === 0" 
          class="text-center py-12 bg-white/80  backdrop-blur-sm rounded-xl shadow-lg transition-colors"
        >
          <div class="relative w-24 h-24 mx-auto mb-4">
            <div class="absolute inset-0 bg-blue-100  rounded-full animate-pulse-slow"></div>
            <img src="/ducksImages/duckIcon.webp" class="w-24 h-24 relative z-10 drop-shadow-xl" />
          </div>
          <h3 class="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-indigo-600  mb-2">No Cruises Yet!</h3>
          <p class="text-gray-600  mb-6">Time to launch your first duck expedition</p>
          <v-btn
            color="primary"
            size="large"
            prepend-icon="mdi-plus"
            @click="dialogState.createCruise = true"
            class="px-6 py-3"
          >
            Create Your First Cruise
          </v-btn>
        </div>

        <!-- Loading State -->
        <div v-else-if="loading" 
          class="flex flex-col items-center justify-center h-64 bg-gradient-to-br from-white/90 via-white/85 to-white/80  backdrop-blur-sm rounded-xl transition-colors border border-gray-100 "
        >
          <div class="relative">
            <img src="/ducksImages/duckLoader.webp" class="w-20 h-20 animate-bounce-slow" alt="Loading Duck" />
            <div class="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-10 h-1 bg-blue-200  rounded-full blur-sm animate-pulse"></div>
          </div>
          <p class="text-gray-600  mt-4 animate-pulse">Loading your fleet...</p>
        </div>

        <!-- Cruise List -->
        <div v-else class="grid gap-6">
          <cruise-card
            v-for="cruise in cruises"
            :key="cruise.id"
            :cruise="cruise"
            :is-duck-list-visible="true"
            :is-resetting="resettingCruise === cruise.id"
            :is-downloading-q-r="downloadingQR === cruise.id"
            :has-new-messages="hasPulsatingIndicator(cruise.id)"
            :max-ducks-allowed="cruise.maxDucksAllowed || 20"
            :ducks-created="cruise.ducks.length || 0"
            @edit="openEditCruiseDialog"
            @reset="resetCruiseDucks"
            @download-qr="downloadQRCodes"
            @open-messages="openCruiseMessages"
            @delete="openDeleteCruiseDialog"
            @toggle-ducks="toggleDuckList"
            @duck-details="openDuckDetails"
            @duck-messages="openDuckMessages"
            @upgrade="dialogState.createCruise = true"
            @buy-more-ducks="dialogState.createCruise = true"
          />
        </div>

        <!-- Dialog Manager -->
        <dialog-manager
          :dialogs="dialogState"
          :loading-states="{ resetting: resettingCruise !== null, deleting: deletingCruise }"
          :selected-items="{
            cruiseId: selectedCruiseId,
            cruiseForEdit: selectedCruiseForEdit,
            duck: selectedDuck,
            duckUuid: selectedDuckUuid
          }"
          :cruise-name="getCruiseName(selectedCruiseId)"
          :duck-name="getDuckName(selectedDuckUuid)"
          @update:dialogs="updateDialogState"
          @cruise-updated="handleCruiseUpdated"
          @duck-created="handleDuckCreated"
          @reset-confirmed="confirmResetDucks"
          @delete-confirmed="confirmDeleteCruise"
          @messages-updated="handleUpdate"
        />

        <!-- QR Template Selector -->
        <q-r-template-selector
          v-model="dialogState.qrTemplateSelector"
          :cruise-name="getCruiseName(selectedCruiseForQR)"
          @template-selected="handleTemplateSelection"
        />
      </div>
    </div>

    <!-- View Toggle -->
    <view-toggle v-model="isDuckView" />
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref, onBeforeUnmount, computed, reactive } from 'vue';
import { useRouter } from 'vue-router';
import type { DetailedCruise, DetailedDuck } from '@/interfaces/cruise';
import { useDemoCruiseStore } from '@/stores/demoCruiseStore';
import { UnderwaterBg } from '@/components/ui/underwaterBg';

// Import components
import AdminHeader from '@/components/demoadmin/AdminHeader.vue';
import CruiseCard from '@/components/demoadmin/CruiseCard.vue';
import DialogManager from '@/components/demoadmin/DialogManager.vue';
import ViewToggle from '@/components/demoadmin/ViewToggle.vue';
import QRTemplateSelector from '@/components/admin/QRTemplateSelector.vue';

const router = useRouter();
const cruiseStore = useDemoCruiseStore();

// View toggle state
const isDuckView = ref(false);

// Dialog state management
const dialogState = reactive({
  createCruise: false,
  editCruise: false,
  addDuck: false,
  duckDetails: false,
  resetConfirm: false,
  deleteCruise: false,
  cruiseMessages: false,
  duckMessages: false,
  qrTemplateSelector: false
});

// Selected items
const selectedCruiseId = ref<number | null>(null);
const selectedCruiseForEdit = ref<DetailedCruise | null>(null);
const selectedCruiseForQR = ref<number | null>(null);
const selectedDuck = ref<DetailedDuck | null>(null);
const selectedDuckUuid = ref<string | null>(null);
const pendingResetCruiseId = ref<number | null>(null);
const cruiseIdToDelete = ref<number | null>(null);

// Data and loading states
const loading = ref(true);
const downloadingQR = ref<number | null>(null);
const resettingCruise = ref<number | null>(null);
const deletingCruise = ref(false);

// Get cruises from the store
const cruises = computed(() => cruiseStore.getAllCruises);

// Message polling variables
const cruisesWithNewMessages = ref<Set<number>>(new Set());

// Reactive state to track visible duck lists
const visibleDuckLists = ref<Set<number>>(new Set());

// Duck activity state
const showDuckActivity = ref(false);

// Lifecycle hooks
onMounted(async () => {
  try {
    loading.value = true;
    
    // Simulate loading time
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Check if any cruises have unread messages
    cruiseStore.cruises.forEach(cruise => {
      if (cruise.unreadMessageCount && cruise.unreadMessageCount > 0) {
        cruisesWithNewMessages.value.add(cruise.id);
      }
    });
  } catch (error) {
    console.error('Failed to initialize demo data:', error);
  } finally {
    loading.value = false;
  }

  // Apply Firefox-specific optimizations
  applyBrowserOptimizations();
});

onBeforeUnmount(() => {
  // Cleanup if needed
});

// Helper functions
const handleLogout = () => {
  router.push('/');  
};

// Toggle duck activity section
const toggleDuckActivity = () => {
  showDuckActivity.value = !showDuckActivity.value;
};

// Message handling
const hasPulsatingIndicator = (cruiseId: number): boolean => {
  return cruisesWithNewMessages.value.has(cruiseId);
};

const getCruiseName = (cruiseId: number | null): string => {
  if (!cruiseId) return '';
  const cruise = cruiseStore.getCruiseById(cruiseId);
  return cruise?.name || 'Cruise';
};

const getDuckName = (duckUuid: string | null): string => {
  if (!duckUuid) return '';
  const duckInfo = cruiseStore.getDuckByUuid(duckUuid);
  if (duckInfo) return `Duck ${duckInfo.duck.id}`;
  return 'Duck';
};

// Dialog state management
const updateDialogState = (newDialogState: typeof dialogState) => {
  Object.assign(dialogState, newDialogState);
};

// Action handlers
const openEditCruiseDialog = (cruise: DetailedCruise) => {
  selectedCruiseForEdit.value = { ...cruise }; // Clone to avoid direct mutation
  dialogState.editCruise = true;
};

const openDuckDetails = (duck: DetailedDuck) => {
  selectedDuck.value = duck;
  dialogState.duckDetails = true;
};

const openCruiseMessages = (cruiseId: number) => {
  selectedCruiseId.value = cruiseId;
  dialogState.cruiseMessages = true;

  // Remove from the set of cruises with new messages
  cruisesWithNewMessages.value.delete(cruiseId);

  // Reset unread message count for the cruise
  const cruise = cruiseStore.getCruiseById(cruiseId);
  if (cruise) {
    cruise.unreadMessageCount = 0;
  }
};

const openDuckMessages = (duckUuid: string) => {
  selectedDuckUuid.value = duckUuid;
  dialogState.duckMessages = true;
};

// Toggle visibility of the duck list for a specific cruise
const toggleDuckList = (cruiseId: number) => {
  // Always ensure the cruise's ducks are visible
  visibleDuckLists.value.add(cruiseId);
};

const downloadQRCodes = async (cruiseId: number) => {
  // Open template selector instead of directly downloading
  selectedCruiseForQR.value = cruiseId;
  dialogState.qrTemplateSelector = true;
};

const handleTemplateSelection = async (template: string) => {
  if (!selectedCruiseForQR.value) return;
  
  try {
    downloadingQR.value = selectedCruiseForQR.value;
    
    // Simulate download with template selection in demo mode
    console.log(`Demo: Generating QR codes for cruise ${selectedCruiseForQR.value} with template: ${template}`);
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Simulate successful download
    console.log(`Demo: QR codes generated successfully with template: ${template}`);
    
    // Close the template selector
    dialogState.qrTemplateSelector = false;
    selectedCruiseForQR.value = null;
  } catch (error) {
    console.error('Failed to generate QR codes:', error);
  } finally {
    downloadingQR.value = null;
  }
};

const resetCruiseDucks = (cruiseId: number) => {
  pendingResetCruiseId.value = cruiseId;
  dialogState.resetConfirm = true;
};

const confirmResetDucks = async () => {
  if (!pendingResetCruiseId.value) return;

  try {
    resettingCruise.value = pendingResetCruiseId.value;
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Reset ducks in the store
    cruiseStore.resetCruiseDucks(pendingResetCruiseId.value);
    
    dialogState.resetConfirm = false;
    
  } catch (error) {
    console.error('Failed to reset cruise ducks:', error);
  } finally {
    resettingCruise.value = null;
    pendingResetCruiseId.value = null;
  }
};

const openDeleteCruiseDialog = (cruiseId: number) => {
  cruiseIdToDelete.value = cruiseId;
  dialogState.deleteCruise = true;
};

const confirmDeleteCruise = async () => {
  if (!cruiseIdToDelete.value) return;
  
  try {
    deletingCruise.value = true;
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Delete cruise from store
    cruiseStore.deleteCruise(cruiseIdToDelete.value);
    
    // Close the dialog
    dialogState.deleteCruise = false;
  } catch (error) {
    console.error('Failed to delete cruise:', error);
  } finally {
    deletingCruise.value = false;
    cruiseIdToDelete.value = null;
  }
};

const handleCruiseUpdated = async () => {
  try {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // The cruise is already updated in the store by the form
    dialogState.editCruise = false;
    
  } catch (error) {
    console.error('Error in handleCruiseUpdated:', error);
  } finally {
    selectedCruiseForEdit.value = null;
  }
};

const handleDuckCreated = async () => {
  try {
    // The duck is already created in the store by the form
    dialogState.addDuck = false;
    
  } catch (error) {
    console.error('Error in handleDuckCreated:', error);
  }
};

const handleUpdate = (updateData: any) => {
  // Handle messages being updated (no notification needed)
  
  // If the update contains a request to open duck messages, handle it
  if (updateData && updateData.openDuckMessages && updateData.duckUuid) {
    // Close the cruise messages dialog
    dialogState.cruiseMessages = false;
    
    // Open the duck messages dialog
    selectedDuckUuid.value = updateData.duckUuid;
    dialogState.duckMessages = true;
  }
};

// Browser optimizations
const applyBrowserOptimizations = () => {
  const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
  
  if (isFirefox) {
    document.documentElement.style.setProperty('--animation-duration', '0.2s');
    document.documentElement.style.scrollBehavior = 'smooth';
  }
};
</script>

<style scoped>
/* Animations */
@keyframes bounce-slow {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.animate-bounce-slow {
  animation: bounce-slow 2s infinite ease-in-out;
}

@keyframes pulse-slow {
  0%, 100% { opacity: 0.6; transform: scale(0.98); }
  50% { opacity: 0.8; transform: scale(1); }
}

.animate-pulse-slow {
  animation: pulse-slow 3s infinite alternate;
}

/* Firefox-specific optimizations */
@-moz-document url-prefix() {
  .firefox-optimize {
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    perspective: 1000px;
    will-change: transform;
  }

  /* Optimize scrolling performance */
  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(155, 155, 155, 0.5) transparent;
  }

  /* Optimize animations */
  .animate-fade-in {
    animation: none;
    opacity: 1;
  }

  /* Improve rendering of backdrop blur */
  .backdrop-blur-sm {
    backdrop-filter: blur(8px);
    -moz-backdrop-filter: blur(8px);
  }

  /* Optimize fixed positioning */
  .fixed {
    transform: translateZ(0);
    will-change: transform;
  }
}

/* Ensure proper spacing and alignment for mobile */
@media (max-width: 640px) {
  .text-justify {
    text-align: justify;
  }

  .break-all {
    word-break: break-word;
  }
}

/* Reduce animation for users who prefer reduced motion */
@media (prefers-reduced-motion: reduce) {
  .animate-bounce-slow,
  .animate-pulse-slow {
    animation: none !important;
  }
}
</style>
